import type { HTMLAttributes } from 'react';

import type { ButtonSize } from '../../types/SharedButtonProps';
import type { IconSize } from '../../types/SharedIconProps';
import type { TextVariant } from '../../types/TextVariant';

type AriaCurrent = HTMLAttributes<HTMLElement>['aria-current'];

/**
 * The type ramp has no `xs` step, so `xs` and `s` share the smallest
 * medium-weight variant — `size` is not a 1:1 map onto `body-semi-${size}`.
 */
const BUTTON_TEXT_VARIANT = {
  xs: 'body-semi-s',
  s: 'body-semi-s',
  m: 'body-semi-m',
} as const satisfies Record<ButtonSize, TextVariant>;

export function resolveButtonTextVariant({
  size,
}: {
  size: ButtonSize;
}): TextVariant {
  return BUTTON_TEXT_VARIANT[size];
}

/**
 * `xs` keeps the 16px glyph rather than dropping to `Icon`'s 12px `xs`: at 12px
 * the icon-only square lands on 22px, under the 24x24 floor in WCAG 2.5.8.
 */
const ICON_ONLY_GLYPH_SIZE = {
  xs: 's',
  s: 's',
  m: 'm',
} as const satisfies Record<ButtonSize, IconSize>;

/** Text buttons pair every size with the 16px glyph; icon-only tracks `size`. */
export function resolveButtonIconSize({
  isIconMode,
  size,
}: {
  isIconMode: boolean;
  size: ButtonSize;
}): IconSize {
  return isIconMode ? ICON_ONLY_GLYPH_SIZE[size] : 's';
}

/** False for `false`, `'false'`, `null`, and `undefined`. */
export const isAriaFalsy = (value: unknown): boolean =>
  value === false || value === 'false';

/**
 * For token attributes (`aria-current`, `aria-selected`, …) where any value
 * except false / 'false' means the state is on.
 */
export const isAriaTruthy = (value: unknown): boolean =>
  value != null && !isAriaFalsy(value);

/**
 * Only `aria-current` highlights an icon button. An open menu trigger keeps its
 * variant's own states: `aria-expanded` flips on mousedown, so highlighting it
 * would replace the pressed treatment while the pointer is still held.
 */
export const isIconButtonHighlighted = (attrs: {
  'aria-current'?: AriaCurrent;
}): boolean => isAriaTruthy(attrs['aria-current']);
